import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { callOutcome, getUsageOptions, type MeteringUsageRow } from '@/entities/metering'
import { cn, errorMessage, formatDurationSec } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { PillTabs } from '@/shared/ui/pill-tabs'
import { SearchInput } from '@/shared/ui/search-input'
import { Spinner } from '@/shared/ui/spinner'

export const Route = createFileRoute('/usage')({
  component: UsagePage,
})

type Period = 'day' | 'week' | 'month'

const PERIOD_HOURS: Record<Period, number> = { day: 24, week: 24 * 7, month: 24 * 30 }

const minutes = (seconds: number) => Math.round(seconds / 60)

// Дозвон — разница двух величин: сколько ядро прожило на попытках, за которые
// клиенту не выставляется ничего.
const dialingSeconds = (row: MeteringUsageRow) =>
  Math.max(0, row.duration_seconds - row.billable_seconds)

// Экспорт нужен компонентному тесту: сам роут собирает его из этого же модуля.
export function UsagePage() {
  const [period, setPeriod] = useState<Period>('week')
  const [accounts, setAccounts] = useState('')

  const query = useMemo(() => {
    const to = new Date()
    const from = new Date(to.getTime() - PERIOD_HOURS[period] * 3600 * 1000)
    const trimmed = accounts.trim()
    return {
      from: from.toISOString(),
      to: to.toISOString(),
      ...(trimmed ? { accounts: trimmed } : {}),
    }
  }, [period, accounts])

  const usage = useQuery(getUsageOptions({ query }))
  const rows = useMemo(() => usage.data?.data ?? [], [usage.data])

  const totals = useMemo(
    () => ({
      calls: rows.reduce((acc, r) => acc + r.calls, 0),
      billable: rows.reduce((acc, r) => acc + r.billable_seconds, 0),
      duration: rows.reduce((acc, r) => acc + r.duration_seconds, 0),
    }),
    [rows],
  )
  // Общая шкала — по прожитому: только на ней видно, какую долю аккаунта съел дозвон.
  const scale = rows.reduce((acc, r) => Math.max(acc, r.duration_seconds), 0)

  return (
    <>
      <PageHeader
        title="Usage"
        description="Потребление по аккаунтам за период: тарифицируемый разговор отдельно от прожитого времени с дозвоном (usage-db)"
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <PillTabs
          aria-label="Период"
          value={period}
          onChange={setPeriod}
          options={[
            { value: 'day', label: 'Сутки' },
            { value: 'week', label: '7 дней' },
            { value: 'month', label: '30 дней' },
          ]}
        />
        <SearchInput
          placeholder="Аккаунты через запятую…"
          className="w-80"
          value={accounts}
          onChange={(e) => setAccounts(e.target.value)}
          aria-label="Фильтр аккаунтов"
        />
      </div>

      {usage.isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : usage.isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить usage"
          description={errorMessage(usage.error)}
          action={
            <Button size="sm" onClick={() => void usage.refetch()}>
              Повторить
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="chart"
          title="Нет данных за период"
          description="Попробуй другой период или фильтр"
        />
      ) : (
        <div className="flex flex-col gap-4">
          <Totals totals={totals} />
          <OutcomeTotals rows={rows} />

          <Card className="p-2">
            <table className="w-full border-separate border-spacing-0">
              <caption className="sr-only">
                Потребление по аккаунтам: звонки, разговор к счёту и прожитое время с дозвоном
              </caption>
              <thead>
                <tr className="text-left text-caption uppercase tracking-[0.1em] text-faint">
                  <th className="px-4 py-3 font-medium">Аккаунт</th>
                  <th className="px-4 py-3 text-right font-medium">Звонки</th>
                  <th className="px-4 py-3 text-right font-medium text-app-fg">
                    Разговор · к счёту
                  </th>
                  <th className="px-4 py-3 text-right font-medium">Прожито · с дозвоном</th>
                  <th className="px-4 py-3 text-right font-medium">Из них дозвон</th>
                  <th className="w-1/4 px-4 py-3 font-medium">Разговор в прожитом</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <UsageRow key={row.account_id} row={row} scale={scale} />
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </>
  )
}

// Две цифры разводятся до таблицы: спутать их — выставить клиенту счёт за
// гудки. Тарифицируется соединение, а не попытка дозвона.
function Totals({ totals }: { totals: { calls: number; billable: number; duration: number } }) {
  const dialing = Math.max(0, totals.duration - totals.billable)
  return (
    // Каждая плитка — именованная область: четыре голых числа рядом обязаны
    // называть себя и скринридеру, и тесту, иначе цифры путаются местами.
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Card role="group" aria-label="Звонков" className="p-5">
        <p className="text-caption uppercase tracking-[0.12em] text-faint">Звонков</p>
        <p className="mt-1 text-heading-lg font-light tabular-nums">{totals.calls}</p>
      </Card>
      <Card role="group" aria-label="Разговор · к счёту" className="p-5 ring-1 ring-accent/40">
        <p className="flex items-center gap-1.5 text-caption uppercase tracking-[0.12em] text-app-fg">
          <Icon name="chart" size={13} />
          Разговор · к счёту
        </p>
        <p className="mt-1 text-heading-lg font-medium tabular-nums">
          {minutes(totals.billable)} <span className="text-body-sm text-muted">мин</span>
        </p>
        <p className="mt-0.5 text-caption text-faint">
          {formatDurationSec(totals.billable)} · от ответа абонента до конца
        </p>
      </Card>
      <Card role="group" aria-label="Прожито · с дозвоном" className="p-5">
        <p className="text-caption uppercase tracking-[0.12em] text-faint">Прожито · с дозвоном</p>
        <p className="mt-1 text-heading-lg font-light tabular-nums text-muted">
          {minutes(totals.duration)} <span className="text-body-sm">мин</span>
        </p>
        <p className="mt-0.5 text-caption text-faint">
          {formatDurationSec(totals.duration)} · счёт по этой цифре не выставляют
        </p>
      </Card>
      <Card role="group" aria-label="Из них дозвон" className="p-5">
        <p className="text-caption uppercase tracking-[0.12em] text-faint">Из них дозвон</p>
        <p className="mt-1 text-heading-lg font-light tabular-nums text-muted">
          {minutes(dialing)} <span className="text-body-sm">мин</span>
        </p>
        <p className="mt-0.5 text-caption text-faint">
          набор и гудки: стоят слота и железа, клиенту — ноль
        </p>
      </Card>
    </div>
  )
}

// Разрез по исходам — из чего сложилась разница между двумя цифрами: за
// «занято» и «нет ответа» тарифицируемых секунд нет вовсе. Незнакомое значение
// показываем как есть: приложение могло прислать исход новее словаря UI.
function OutcomeTotals({ rows }: { rows: MeteringUsageRow[] }) {
  const totals = new Map<string, number>()
  for (const row of rows) {
    for (const [outcome, count] of Object.entries(row.by_outcome ?? {})) {
      totals.set(outcome, (totals.get(outcome) ?? 0) + count)
    }
  }
  const items = [...totals.entries()].sort((a, b) => b[1] - a[1])
  if (items.length === 0) return null

  return (
    <Card
      role="group"
      aria-label="Звонки по исходам"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 p-4"
    >
      <span className="text-body-sm text-muted">Звонки по исходам:</span>
      {items.map(([outcome, count]) => (
        <span key={outcome} className="flex items-center gap-1.5 text-body-sm">
          <span className={cn('text-muted', outcome === 'answered' && 'text-app-fg')}>
            {callOutcome(outcome).label}
          </span>
          <Chip tone={outcome === 'answered' ? 'accent' : 'neutral'}>{count}</Chip>
        </span>
      ))}
    </Card>
  )
}

function UsageRow({ row, scale }: { row: MeteringUsageRow; scale: number }) {
  const dialing = dialingSeconds(row)
  const width = scale > 0 ? (row.duration_seconds / scale) * 100 : 0
  const billableShare = row.duration_seconds > 0 ? row.billable_seconds / row.duration_seconds : 0

  return (
    <tr className="text-body-sm">
      <td className="border-t border-divider px-4 py-3 font-medium">{row.account_id}</td>
      <td className="border-t border-divider px-4 py-3 text-right tabular-nums">{row.calls}</td>
      <td className="border-t border-divider px-4 py-3 text-right font-medium tabular-nums">
        {formatDurationSec(row.billable_seconds)}
      </td>
      <td className="border-t border-divider px-4 py-3 text-right tabular-nums text-muted">
        {formatDurationSec(row.duration_seconds)}
      </td>
      <td className="border-t border-divider px-4 py-3 text-right tabular-nums text-muted">
        {dialing > 0 ? formatDurationSec(dialing) : '—'}
      </td>
      <td className="border-t border-divider px-4 py-3">
        {/* Полоса дублирует обе цифры слева — скринридеру она лишний шум. */}
        <div aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-raise">
          <div className="h-full rounded-full bg-outline" style={{ width: `${width}%` }}>
            <div
              className="h-full rounded-full bg-accent"
              style={{ width: `${billableShare * 100}%` }}
            />
          </div>
        </div>
      </td>
    </tr>
  )
}
